// ── How credit consumption is presented ─────────────────────────────────────
//
// These live apart from useCreditBalances on purpose. They were originally
// exported from the hook, which coupled them to it in a way that only showed up
// under test: every consumer that does
//
//     jest.mock('../hooks/useCreditBalances', () => ({ useCreditBalances: ... }))
//
// replaces the WHOLE module, so the constants vanished and an unrelated
// presentational component crashed on `undefined.map`. A component's labels
// should not depend on whether someone stubbed a data hook.
//
// They are also simply not hook state: ordering, wording and pluralisation are
// display decisions, and keeping them here means the usage card and anything
// else describing consumption cannot drift apart on what to call things.

/**
 * The actions credits are spent on, in the order the UI lists them.
 *
 * Ordered deliberately and not alphabetically: SEO content first because it is
 * the costliest action and the one most people are here for, then AI images.
 * The usage card renders in this order and gives both the same visual weight,
 * so the ranking a reader perceives comes from the numbers rather than from the
 * layout.
 *
 * Keyword lists used to be a third action. They are free now — the server no
 * longer charges for them — so they are not something credits are spent on and
 * are left out of the usage card and its total. The server still sends a
 * `keywords` entry; it is simply not read.
 */
export const USAGE_ACTIONS = ['content', 'images'];

/** What each row is called. */
export const USAGE_ACTION_LABELS = {
  content: 'SEO content',
  images: 'AI images',
};

/** What each action produces, for the "12 posts" half of a row. */
export const USAGE_ACTION_NOUNS = {
  content: { one: 'post', many: 'posts' },
  images: { one: 'image', many: 'images' },
};

/** The shape the usage card expects, with everything at zero. */
export const EMPTY_USAGE = {
  byAction: {
    content: { credits: 0, generations: 0 },
    images: { credits: 0, generations: 0 },
  },
  totalCredits: 0,
  allotment: 0,
};

/**
 * Read one action out of a usage payload, tolerating anything.
 *
 * The server sends `{ credits, generations }` per action. A CMS deployed ahead
 * of the API — or a test stubbing the hook without this field — must render
 * zeroes rather than NaN, so every access goes through here.
 */
export function usageFor(usage, action) {
  const entry = usage?.byAction?.[action];
  if (!entry || typeof entry !== 'object') return { credits: 0, generations: 0 };
  return {
    credits: Number(entry.credits) || 0,
    generations: Number(entry.generations) || 0,
  };
}

/** "12 posts" / "1 post" — singular matters at the boundary people notice. */
export function generationCountLabel(action, n) {
  const noun = USAGE_ACTION_NOUNS[action];
  if (!noun) return `${Number(n) || 0}`;
  const count = Number(n) || 0;
  return `${count.toLocaleString()} ${count === 1 ? noun.one : noun.many}`;
}
